"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  RBAC_ACTION_COLUMNS,
  RBAC_MATRIX_ROWS,
  RBAC_ROLES,
  getRowPermission,
  type MarketingWorkspaceRole,
  type RbacPermissionKey,
  rbacPermissionCount,
  rbacRoleHasPermission,
} from "./RbacVisual.constants";
import type { RbacVisualProps } from "./RbacVisual.types";

type CellState = "disabled" | "unchecked" | "checked";

const getCellState = (
  permission: RbacPermissionKey | null,
  activeRole: MarketingWorkspaceRole,
): CellState => {
  if (permission === null) return "disabled";
  return rbacRoleHasPermission(activeRole, permission)
    ? "checked"
    : "unchecked";
};

const MatrixCheckbox = ({
  state,
  label,
}: {
  state: CellState;
  label: string;
}) => (
  <span
    role="checkbox"
    aria-checked={state === "checked"}
    aria-disabled={state === "disabled"}
    aria-label={label}
    className={cn(
      "inline-flex size-5 shrink-0 items-center justify-center rounded border transition-[border-color,background-color,opacity] duration-200 ease-out sm:size-6",
      state === "checked" &&
        "border-sky-400/60 bg-sky-500/20 text-sky-700 dark:text-sky-300",
      state === "unchecked" &&
        "border-zinc-300/90 bg-transparent dark:border-white/25",
      state === "disabled" &&
        "border-zinc-200/50 bg-zinc-100/40 opacity-35 dark:border-white/[0.06] dark:bg-white/[0.02]",
    )}
  >
    <AnimatePresence mode="popLayout" initial={false}>
      {state === "checked" ? (
        <motion.span
          key="check"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
          className="flex items-center justify-center"
        >
          <Check className="size-3 sm:size-3.5" aria-hidden />
        </motion.span>
      ) : null}
    </AnimatePresence>
  </span>
);

export const RbacVisual = ({ variant = "compact" }: RbacVisualProps) => {
  const prefersReducedMotion = useReducedMotion();
  const [activeRole, setActiveRole] =
    useState<MarketingWorkspaceRole>("Administrator");
  const isWide = variant === "wide";

  useEffect(() => {
    if (prefersReducedMotion) return;
    let index = 0;
    const interval = window.setInterval(() => {
      index = (index + 1) % RBAC_ROLES.length;
      setActiveRole(RBAC_ROLES[index] ?? "Administrator");
    }, 2800);
    return () => window.clearInterval(interval);
  }, [prefersReducedMotion]);

  return (
    <div
      className={cn(
        "relative flex w-full items-center justify-center",
        isWide ? "h-full min-h-0" : "h-auto",
      )}
    >
      <div className="flex w-full max-w-[520px] shrink-0 flex-col gap-2.5 sm:max-w-[560px]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {RBAC_ROLES.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => setActiveRole(role)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-200 sm:text-sm",
                  activeRole === role
                    ? "border-sky-400/40 bg-sky-400/15 text-sky-700 dark:text-sky-200"
                    : "border-zinc-200 bg-white/80 text-zinc-500 dark:border-white/[0.05] dark:bg-[#14161c] dark:text-white/45",
                )}
              >
                {role}
              </button>
            ))}
          </div>
          <motion.span
            key={activeRole}
            initial={prefersReducedMotion ? false : { opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="shrink-0 rounded-full bg-sky-400/15 px-2.5 py-1 text-xs font-medium text-sky-700 dark:text-sky-200 sm:text-sm"
          >
            {rbacPermissionCount(activeRole)} perms
          </motion.span>
        </div>

        <div className="overflow-hidden rounded-xl border border-zinc-200/50 bg-white/70 dark:border-white/[0.05] dark:bg-[#14161c]/90">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[40%]" />
              <col className="w-[20%]" />
              <col className="w-[20%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-zinc-200/50 dark:border-white/[0.05]">
                <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-white/45 sm:text-xs">
                  Resource
                </th>
                {RBAC_ACTION_COLUMNS.map((column) => (
                  <th
                    key={column}
                    className="px-2 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-white/45 sm:text-xs"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {RBAC_MATRIX_ROWS.map((row) => (
                <tr
                  key={row.resource}
                  className="border-b border-zinc-200/70 last:border-b-0 dark:border-white/[0.06]"
                >
                  <td className="truncate px-3 py-2 text-left text-xs font-medium text-zinc-800 dark:text-white/85 sm:py-2.5 sm:text-sm">
                    {row.resource}
                  </td>
                  {RBAC_ACTION_COLUMNS.map((column) => {
                    const permissionKey = getRowPermission(row, column);
                    const state = getCellState(permissionKey, activeRole);

                    return (
                      <td
                        key={column}
                        className="px-2 py-2 text-center sm:py-2.5"
                      >
                        <div className="flex justify-center">
                          <MatrixCheckbox
                            state={state}
                            label={`${row.resource}: ${column}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
